/**
 * App shell + routing (ADR-0010 React Router).
 *
 * RESIDENT surfaces (eager — the intake is the LCP-critical wedge, NFR-15):
 *   /report        → intake ("Report a Concern") — supports chromeless direct-iframe embed
 *                     (?embed=1&chrome=off), same pattern as /faq, /meetings, /concerns,
 *                     /contractors (report-form-fullpage-embed)
 *   /confirmation  → post-submit tracking-code hero — same chromeless support, since a successful
 *                     /report submit navigates here inside the same embed
 *   /track/:code   → public tracking view (public projection only, NFR-3)
 *   /track         → code lookup
 *   /ask           → "Ask a Question" intake (ask-a-question-faq.md §5.1) — shares /confirmation
 *   /records-request → "Request Public Records" (Open Records/KORA) intake (records-request.md) —
 *                       shares /confirmation
 *   /apply-permit  → "Apply for a Permit" (Permits & Licensing, roadmap module 6) intake — shares
 *                     /confirmation
 *   /faq           → public FAQ browse + search (§5.3)
 *   /faq/:id       → a single published FAQ entry
 *   /meetings      → Council Agenda + Packet public list (agenda-packet.md §6) — published +
 *                     archived meetings, upcoming first
 *   /meetings/:id  → the accessible HTML meeting packet (agenda + minutes + notice block)
 *   /concerns      → public "Reported Concerns" map + accessible status list (public-concerns-map.md
 *                     / ADR-0023) — no login, PII-free fuzzed-location pins + category/status/date
 *                     filters
 *   /contractors   → public Contractor Registry directory (docs/design/contractor-registry/spec.md
 *                     §3c) — no login, business/contact/phone/trades list filterable by trade +
 *                     search, "currently registered" only
 *   /notify/subscribe          → "Get city updates" subscribe form + channel picker
 *   /notify/confirm/:token     → double opt-in confirm landing
 *   /notify/unsubscribe/:token → one-click unsubscribe landing
 *   /notify/preferences/:token → tokenised "manage your updates" channel preferences page
 *   /notify/manage             → "email me my manage link" self-service recovery (no login,
 *                                 subscriber-self-service.md Part A)
 *
 * STAFF/ADMIN surfaces (lazy — code-split so they never weigh down the resident bundle):
 *   /staff/login, /staff/queue, /staff/reports/:id,
 *   /staff/admin/{categories,staff,branding,notifications}
 *
 * /widget-demo → a host-page harness that mounts the framework-free launcher (apps/widget).
 *
 * `embed=1` (ADR-0004) renders the compact iframe chrome. Root redirects to /report.
 */
import { lazy, Suspense } from "react";
import { Navigate, Route, Routes, useSearchParams } from "react-router-dom";
import { TopProgressBar } from "./components/TopProgressBar.js";
import { IntakePage } from "./pages/IntakePage.js";
import { ConfirmationPage } from "./pages/ConfirmationPage.js";
import { TrackingPage } from "./pages/TrackingPage.js";
import { AskPage } from "./pages/AskPage.js";
import { RecordsRequestPage } from "./pages/RecordsRequestPage.js";
import { ApplyForPermitPage } from "./pages/ApplyForPermitPage.js";
import { FaqPage } from "./pages/FaqPage.js";
import { MeetingsPage } from "./pages/MeetingsPage.js";
import { ConcernsMapPage } from "./pages/ConcernsMapPage.js";
import { ContractorsDirectoryPage } from "./pages/ContractorsDirectoryPage.js";
import { SubscribePage } from "./pages/notify/SubscribePage.js";
import { NotifyLandingPage } from "./pages/notify/NotifyLandingPage.js";
import { PreferencesPage } from "./pages/notify/PreferencesPage.js";
import { ManageLinkPage } from "./pages/notify/ManageLinkPage.js";

const StaffApp = lazy(() => import("./staff/StaffApp.js").then((m) => ({ default: m.StaffApp })));
const WidgetDemoPage = lazy(() =>
  import("./pages/WidgetDemoPage.js").then((m) => ({ default: m.WidgetDemoPage })),
);

function Loading(): JSX.Element {
  return (
    <p role="status" style={{ padding: "2rem", textAlign: "center" }}>
      Loading…
    </p>
  );
}

export function App(): JSX.Element {
  const [params] = useSearchParams();
  const embed = params.get("embed") === "1";
  // "meetings" inline-embed launcher only (meetings-embed-launcher dispatch) — mirrors the
  // ordinance viewer's own chrome-stripping runtime param. Plain `?embed=1` (no `chrome=off`)
  // keeps today's TownHeader-suppressed-but-H1/subtitle/Subscribe-shown behavior for backward
  // compat with the raw iframe embed already documented in
  // docs/runbooks/linnvalley-meetings-embed.md.
  const chromeless = embed && params.get("chrome") === "off";

  return (
    <Suspense fallback={<Loading />}>
      <TopProgressBar />
      <Routes>
        <Route
          path="/"
          element={
            <Navigate to={{ pathname: "/report", search: window.location.search }} replace />
          }
        />
        <Route path="/report" element={<IntakePage embed={embed} chromeless={chromeless} />} />
        <Route
          path="/confirmation"
          element={<ConfirmationPage embed={embed} chromeless={chromeless} />}
        />
        <Route path="/track" element={<TrackingPage />} />
        <Route path="/track/:code" element={<TrackingPage />} />
        <Route path="/ask" element={<AskPage embed={embed} />} />
        <Route path="/records-request" element={<RecordsRequestPage embed={embed} />} />
        <Route path="/apply-permit" element={<ApplyForPermitPage embed={embed} />} />
        <Route path="/faq" element={<FaqPage embed={embed} chromeless={chromeless} />} />
        <Route path="/faq/:id" element={<FaqPage embed={embed} chromeless={chromeless} />} />
        <Route path="/meetings" element={<MeetingsPage embed={embed} chromeless={chromeless} />} />
        <Route
          path="/meetings/:id"
          element={<MeetingsPage embed={embed} chromeless={chromeless} />}
        />
        <Route path="/concerns" element={<ConcernsMapPage embed={embed} chromeless={chromeless} />} />
        <Route
          path="/contractors"
          element={<ContractorsDirectoryPage embed={embed} chromeless={chromeless} />}
        />
        <Route path="/notify/subscribe" element={<SubscribePage embed={embed} />} />
        <Route
          path="/notify/confirm/:token"
          element={<NotifyLandingPage kind="confirm" embed={embed} />}
        />
        <Route
          path="/notify/unsubscribe/:token"
          element={<NotifyLandingPage kind="unsubscribe" embed={embed} />}
        />
        <Route path="/notify/preferences/:token" element={<PreferencesPage embed={embed} />} />
        <Route path="/notify/manage" element={<ManageLinkPage embed={embed} />} />
        <Route path="/widget-demo" element={<WidgetDemoPage />} />
        <Route path="/staff/*" element={<StaffApp />} />
        <Route path="*" element={<Navigate to="/report" replace />} />
      </Routes>
    </Suspense>
  );
}
